///<<reference path="../types/box2d.d.ts" />

import { mat2, vec2 } from "@tlaukkan/tsm";
import { Entity, System } from "ecsy";
import { applyImpulse, claimVec2, clearAllJoints, passVec2, passVec2FromComponents } from "../box2dUtils";
import { Antibody, Magnet, BloodCell, ColliderShape, Collision, Cytokine, Eat, ElementInfo, Grow, Physics, Transform, Virus, VirusPointAttractor } from "../components";
import { CollisionCategory, Optimizations, PhysicsConstants } from "../constants";
import { getEntityById, getId } from "../entityFactory";
import { entityTrace, getEntityDetails } from "../entityTrace";
import { PerfTracker } from "../performanceTracking";
import { addComponent, getOrAddComponent, printObjectProperties, rotateAround, uniformVec2 } from "../utils";
import { Game } from "../game";

/*Namespace for Box2D functions*/

export class PhysicsSystem extends System {
	gravity: B2D.b2Vec2;
	b2world: B2D.b2World;
	totalTime: number = 0;


	static bodyToEntitiesMap = new Map<B2D.b2Body, Set<Entity>>();
	static fixtureToEntityMap = new Map<B2D.b2Fixture, Entity>();
	static world: B2D.b2World;

	private forcedCollisionsBuffer = new Array<[number, number, boolean]>();

	bodyPool: Array<B2D.b2Body> | null = null;
	private game: Game;

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;

		//Emscripted WebIDL Binder import means.
		(window as any).Box2D().then((Box2D: any) => {
			/**
			 * Super hack. 
			 * 
			 * We use the old school <reference path> syntax to make available the 
			 * Box2D typings as an ambient namespace. JavaScript won't understand this as it is types
			 * only, but Typescript does. 
			 * 
			 * Then we create this global variable so the same syntax
			 * to reference the types happens to refer to the real object.
			 */

			if (process.env.is_server) {
				(global as any).B2D = Box2D;
			}
			else {
				(window as any).B2D = Box2D;
			}

			//Useful for checking actual signiture vs ported type defs
			//printObjectProperties(Box2D);
		});

		this.gravity = new B2D.b2Vec2(0.0, 0.0);

		this.b2world = new B2D.b2World(this.gravity, false);

		PhysicsSystem.world = this.b2world;
	}

	init() {
		const onCollision = (contact: B2D.b2Contact, fixtureA: B2D.b2Fixture, fixtureB: B2D.b2Fixture,) => {
			//Determine if it is a damaging collision or non-damaging
			const filterA = fixtureA.GetFilterData();
			const categoryA = filterA.get_categoryBits();

			const filterB = fixtureB.GetFilterData();
			const categoryB = filterB.get_categoryBits();

			const AisVirus = categoryA === CollisionCategory.Virus || categoryA === CollisionCategory.Virophage;
			const BisVirus = categoryB === CollisionCategory.Virus || categoryB === CollisionCategory.Virophage;
			const oneIsVirus = (AisVirus && !BisVirus) || (!AisVirus && BisVirus);

			const entityA = PhysicsSystem.fixtureToEntityMap.get(fixtureA)
			const entityB = PhysicsSystem.fixtureToEntityMap.get(fixtureB)

			if (entityA && entityB) {

				entityTrace(entityA, `(Step ${getStepNumber()}) collision detected with [${getEntityDetails(entityB)}]`);
				entityTrace(entityB, `(Step ${getStepNumber()}) collision detected with [${getEntityDetails(entityA)}]`);

				//Don't collide with creator. For spawned things
				const infoA = entityA.getComponent(ElementInfo);
				const infoB = entityB.getComponent(ElementInfo);
				if (infoA && infoB && (infoA.creatorId === infoB.id || infoB.creatorId === infoA.id)) {
					contact.SetEnabled(false);
					return;
				}

				const colA = getOrAddComponent(entityA, Collision);
				colA.collidedWith.push({ entity: entityB, canDamage: oneIsVirus });

				const colB = getOrAddComponent(entityB, Collision);
				colB.collidedWith.push({ entity: entityA, canDamage: oneIsVirus });
			}
			else {
				console.log("Physics: Two fixtures collided where one didn't have an entity.");
			}
		}

		const onSensorOverlap = (sensor: B2D.b2Fixture, collided: B2D.b2Fixture) => {
			const entity = PhysicsSystem.fixtureToEntityMap.get(sensor);
			const hit = PhysicsSystem.fixtureToEntityMap.get(collided);
			if (entity && hit) {
				const colision = getOrAddComponent(entity, Collision);
				colision.collidedWith.push({ entity: hit, canDamage: false });
			}
		}

		const collisionListenser = new B2D.JSContactListener();

		collisionListenser.BeginContact = (contactPtr) => {
			const contact = B2D.wrapPointer(contactPtr, B2D.b2Contact);
			const fixtureA = contact.GetFixtureA();
			const fixtureB = contact.GetFixtureB();
			if (fixtureA.IsSensor()) { onSensorOverlap(fixtureA, fixtureB) }
			if (fixtureB.IsSensor()) { onSensorOverlap(fixtureB, fixtureA) }
		}
		collisionListenser.EndContact = () => { }

		collisionListenser.PreSolve = (contactPtr: any, manifoldPtr: any) => {
			const contact = B2D.wrapPointer(contactPtr, B2D.b2Contact);
			const fixtureA = contact.GetFixtureA()
			const fixtureB = contact.GetFixtureB()
			onCollision(contact, fixtureA, fixtureB);
		}
		collisionListenser.PostSolve = () => { }
		this.b2world.SetContactListener(collisionListenser);


		//Initialize the physics pool
		if (Optimizations.PhysicsBodyPooling) {
			console.time("Initialize physics pool");
			this.bodyPool = [];
			for (let i = 0; i < 500; ++i) {
				const bd = new B2D.b2BodyDef();
				bd.set_type(B2D.b2_dynamicBody);
				const body = this.b2world.CreateBody(bd);
				body.SetGravityScale(0.0);
				body.SetAngularDamping(5);
				body.SetAwake(false);
				body.SetActive(false);
				this.bodyPool.push(body);
			}
			console.timeEnd("Initialize physics pool");
		}
	}

	//Create a collision event without the physics system. For network sync
	causeCollision(entity: number, collidedWith: number, canDamage: boolean) {
		this.forcedCollisionsBuffer.push([entity, collidedWith, canDamage]);
	}

	destroy() {
		B2D.destroy(this.b2world);
	}

	execute() {
		const dt = PhysicsConstants.StepRate;

		PerfTracker.startSubsystem("Adding bodies");
		if ((window as any).debugPhysics) console.time("physics 1");
		this.queries.physics.added?.forEach((entity: Entity) => {
			const physics = entity.getMutableComponent(Physics);

			const body = this.createBody(entity, physics.initialPosition, physics.initialRotation);
			body.SetBullet(physics.isHighSpeed);
			let fixture = null;
			if (physics.shapeType === ColliderShape.Circle) {
				fixture = this.createCircleFixture(entity, body, physics.isSensor);
				physics.mass = (Math.pow(physics.size.x / 2, 2) * Math.PI) * physics.weightClass;
			} else {
				fixture = this.createBoxFixture(entity, body, physics.isSensor);
				physics.mass = (physics.size.x * physics.size.y) * physics.weightClass;
			}
			entityTrace(entity, `created physics component and fixture`);

			physics.body = body;
			physics.fixture = fixture;

			addComponent(entity, Transform, { size: physics.baseSize, shape: physics.shapeType })

			if (physics.initialPush) {
				applyImpulse(entity, physics.initialPush, physics.initialPosition);
				physics.initialPush = null;
			}
		});
		PerfTracker.endSubsystem();


		PerfTracker.startSubsystem("Removing bodies");
		this.queries.physics.removed?.forEach((entity: Entity) => {
			const physics = entity.getRemovedComponent(Physics);
			const body = physics.body;
			PhysicsSystem.fixtureToEntityMap.delete(physics.fixture);
			physics.body.DestroyFixture(physics.fixture);

			const entities = PhysicsSystem.bodyToEntitiesMap.get(body);
			if (entities) {
				entities.delete(entity)
				entityTrace(entity, `destroyed fixture`);
			}
			if (!entities || entities.size === 0) {
				this.destroyBody(body);
				entityTrace(entity, `destroyed physics body`);
			}
		});
		PerfTracker.endSubsystem();

		PerfTracker.startSubsystem("Growing and misc");
		while (this.queries.growEffects.results.length > 0) {
			const entity = this.queries.growEffects.results[0];
			const physics = entity.getMutableComponent(Physics);
			physics.size.add(uniformVec2(0.5));
			PhysicsSystem.fixtureToEntityMap.delete(physics.fixture);
			physics.body.DestroyFixture(physics.body.GetFixtureList()); //assumption of no compound body here
			const fixture = this.createCircleFixture(entity, physics.body, physics.isSensor);
			physics.fixture = fixture;
			entity.removeComponent(Grow);
			entityTrace(entity, `grow effect executed`);
		}

		//Clean up any collisions that were added last frame (they get added during the world step)
		while (this.queries.collisionsToCleanUp.results.length > 0) {
			const entity = this.queries.collisionsToCleanUp.results[0];
			entity.removeComponent(Collision, true);
		}

		this.forcedCollisionsBuffer.forEach(forcedCollision => {
			const hit = getEntityById(forcedCollision[0]);
			const other = getEntityById(forcedCollision[1]);
			const canDamage = forcedCollision[2];
			if (!hit || !other) throw new Error("EventSync: Collision received for non-existent entity");
			{
				const colision = getOrAddComponent(hit, Collision)
				colision.collidedWith.push({ entity: other, canDamage });
			}
			entityTrace(hit, `client collision ${!canDamage ? '(damaging)' : ''} with [${getEntityDetails(other)}]`)
		})
		this.forcedCollisionsBuffer = [];
		PerfTracker.endSubsystem();

		PerfTracker.startSubsystem("Physics step");
		this.b2world.Step(dt, 12, 6);
		PerfTracker.endSubsystem();

		PerfTracker.startSubsystem("Updating transforms");
		const pos = new vec2();
		const rotate = new mat2()
		this.queries.physics.results.forEach(entity => {
			const physics = entity.getComponent(Physics);
			const transform = entity.getMutableComponent(Transform);

			pos.xy = claimVec2(physics.body.GetPosition()).xy;
			let rot = physics.body.GetAngle();

			//Update rotation of fixtures
			if (!physics.fixtureOffset.equals(vec2.zero)) {
				const offset = physics.fixtureOffset.copy();
				rotate.setIdentity();
				rotate.rotate(rot);
				offset.multiplyMat2(rotate);
				pos.add(offset)
			}
			transform.position = pos.copy();

			transform.rotation = rot;
			//transform.forward = rotateAround(new vec2([0, 1]), rot, vec2.zero.copy()); //calculate lazily, optimization
			transform.size = physics.baseSize;
			transform.scale = physics.size.x / physics.baseSize.x;
		})
		PerfTracker.endSubsystem();

		this.totalTime += dt;
	}

	createBody(entity: Entity, initialPosition: vec2, initialRotation: number) {
		//Recycle body from pool if possible
		let body: B2D.b2Body;
		if (this.bodyPool && this.bodyPool.length > 0) {
			body = this.bodyPool.pop()!;
		}
		else {
			const bd = new B2D.b2BodyDef();
			bd.set_type(B2D.b2_dynamicBody);
			body = this.b2world.CreateBody(bd);
		}

		body.SetGravityScale(0.0);
		body.SetAngularDamping(5);

		const pos = passVec2(initialPosition);
		body.SetTransform(pos, initialRotation);
		body.SetLinearVelocity(passVec2(vec2.zero.copy()));
		body.SetLinearDamping(PhysicsConstants.DefaultDamping);
		body.SetAwake(true);
		body.SetActive(true);

		const entities = new Set<Entity>([entity]);
		PhysicsSystem.bodyToEntitiesMap.set(body, entities);
		return body;
	}

	destroyBody(body: B2D.b2Body) {
		PhysicsSystem.bodyToEntitiesMap.delete(body);

		if (this.bodyPool) {
			clearAllJoints(body);
			body.SetLinearVelocity(new B2D.b2Vec2(0, 0));
			body.SetAngularVelocity(0);
			body.SetLinearDamping(PhysicsConstants.DefaultDamping); // Reset to default, not 0
			body.SetAngularDamping(5); // Reset to default
			body.SetGravityScale(0.0); // Reset to default
			body.SetBullet(false); // Reset bullet flag
			body.SetAwake(false);
			body.SetActive(false);
			this.bodyPool.push(body);
		}
		else {
			this.b2world.DestroyBody(body!);
		}
	}

	createCircleFixture(entity: Entity, body: B2D.b2Body, isSensor: boolean, localPosition?: vec2) {
		const physics = entity.getMutableComponent(Physics);
		const radius = physics.size.x / 2;
		const shape = new B2D.b2CircleShape();
		shape.set_m_radius(radius);
		if (localPosition) {
			shape.set_m_p(passVec2(localPosition));
			physics.fixtureOffset = localPosition;
		}
		else {
			physics.fixtureOffset = vec2.zero.copy();
		}
		const fixture = body.CreateFixture(shape, physics.weightClass);
		fixture.SetRestitution(1);
		if (isSensor) {
			fixture.SetSensor(true);
		}

		const filterData = new B2D.b2Filter()
		filterData.set_categoryBits(0x0000);
		fixture.SetFilterData(filterData)

		this.applyCollisionFilter(entity, fixture);
		PhysicsSystem.fixtureToEntityMap.set(fixture, entity);
		const entitiesOnBody = PhysicsSystem.bodyToEntitiesMap.get(body)!;
		entitiesOnBody.add(entity);
		return fixture;
	}

	createBoxFixture(entity: Entity, body: B2D.b2Body, isSensor: boolean, localPosition?: vec2) {
		const physics = entity.getMutableComponent(Physics);
		const shape = new B2D.b2PolygonShape();
		if (localPosition) {
			physics.fixtureOffset = localPosition;
		}
		else {
			physics.fixtureOffset = vec2.zero.copy();
		}
		//TODO: update types
		shape.SetAsBox(physics.size.x / 2, physics.size.y / 2);
		//@ts-ignore
		shape.set_m_centroid(physics.initialPosition);

		const fixture = body.CreateFixture(shape, physics.weightClass);
		fixture.SetRestitution(1);
		if (isSensor) {
			fixture.SetSensor(true);
		}

		const filterData = new B2D.b2Filter()
		filterData.set_categoryBits(0x0000);
		fixture.SetFilterData(filterData)

		this.applyCollisionFilter(entity, fixture);
		PhysicsSystem.fixtureToEntityMap.set(fixture, entity);
		const entitiesOnBody = PhysicsSystem.bodyToEntitiesMap.get(body)!;
		entitiesOnBody.add(entity);
		return fixture;
	}

	applyCollisionFilter(entity: Entity, fixture: B2D.b2Fixture) {
		//Collison only happens on the server during simulation
		if (!process.env.is_server || !this.game.inSimulation) return;
		const filterData = new B2D.b2Filter()

		if (entity.hasComponent(Antibody)) {
			filterData.set_categoryBits(CollisionCategory.Antibody);
			filterData.set_maskBits(CollisionCategory.Virus | CollisionCategory.Virophage | CollisionCategory.Cell | CollisionCategory.Antibody);
		}
		else if (entity.hasComponent(Cytokine)) {
			filterData.set_categoryBits(CollisionCategory.Cytokine);
			filterData.set_maskBits(CollisionCategory.Virus | CollisionCategory.Virophage | CollisionCategory.Cell);
		}
		else if (entity.hasComponent(Virus)) {
			if (entity.hasComponent(Eat) || entity.hasComponent(VirusPointAttractor)) {
				filterData.set_categoryBits(CollisionCategory.Virophage);
				filterData.set_maskBits(CollisionCategory.Antibody | CollisionCategory.Cell | CollisionCategory.Cytokine | CollisionCategory.Virus | CollisionCategory.Virophage);
			} else {
				filterData.set_categoryBits(CollisionCategory.Virus);
				filterData.set_maskBits(CollisionCategory.Antibody | CollisionCategory.Cell | CollisionCategory.Cytokine | CollisionCategory.Virophage);
			}
		}
		else if (entity.hasComponent(BloodCell)) {
			filterData.set_categoryBits(CollisionCategory.Cell);
			filterData.set_maskBits(CollisionCategory.Virus | CollisionCategory.Virophage | CollisionCategory.Antibody);
		}

		fixture.SetFilterData(filterData)
	}

	//Unused but might want later
	reducePrecision(physics: Physics, precision: number) {
		const pos = claimVec2(physics.body.GetPosition());
		let rot = physics.body.GetAngle();
		pos.x = +pos.x.toFixed(precision);
		pos.y = +pos.y.toFixed(precision);
		rot = +rot.toFixed(precision);
		physics.body.SetTransform(passVec2(pos), rot);
		const velocity = claimVec2(physics.body.GetLinearVelocity());
		velocity.x = +velocity.x.toFixed(precision);
		velocity.y = +velocity.y.toFixed(precision);
		physics.body.SetLinearVelocity(passVec2(velocity));
		let angV = physics.body.GetAngularVelocity();
		physics.body.SetAngularVelocity(+angV.toFixed(precision));
	}

	mergeBodies(entities: Entity[]) {
		if (entities.length < 2) return;
		entities.sort((a, b) => (getId(a) || 0) - (getId(b) || 0)) //make sure order of merging in well-defined

		const sumOfPositions = entities
			.map(entity => {
				const transform = entity.getComponent(Transform);
				return transform.position.copy();
			})
			.reduce((prev, curr) => prev.add(curr));
		const averagePosition = sumOfPositions.scale(1 / entities.length);

		const consumerPhysics = entities[0].getMutableComponent(Physics);
		const consumerBody = consumerPhysics.body;
		consumerBody.SetTransform(passVec2(averagePosition), consumerBody.GetAngle());

		for (let i = 0; i < entities.length; ++i) {
			const other = entities[i];
			const otherTransform = other.getComponent(Transform);
			const otherPhysics = other.getMutableComponent(Physics);
			const otherBody = otherPhysics.body;

			const deadFixtures = new Array<B2D.b2Fixture>();
			for (let fixture = otherBody.GetFixtureList(); fixture.a !== 0; fixture = fixture.GetNext()) {
				const currentEntity = PhysicsSystem.fixtureToEntityMap.get(fixture);
				if (!currentEntity || !currentEntity.hasComponent(Physics)) {
					throw new Error(`ChainSystem: Trying to merge invalid bodies`);
				};
				entityTrace(currentEntity, `merging into a compound body`);
				const currentPhysics = currentEntity.getComponent(Physics);

				const shape = B2D.castObject(fixture.GetShape(), B2D.b2CircleShape);
				const offset = claimVec2(shape.get_m_p());
				//const sharedBodyPosition = claimVec2(consumerPhysics.body.GetPosition());
				const sharedBodyPosition = averagePosition;
				const localPosition = otherTransform.position.copy().subtract(sharedBodyPosition);
				localPosition.add(offset);
				deadFixtures.push(currentPhysics.fixture);
				PhysicsSystem.fixtureToEntityMap.delete(currentPhysics.fixture);
				if (currentPhysics.shapeType === ColliderShape.Circle) {
					currentPhysics.fixture = this.createCircleFixture(currentEntity, consumerBody, currentPhysics.isSensor, localPosition);
				} else {
					currentPhysics.fixture = this.createBoxFixture(currentEntity, consumerBody, currentPhysics.isSensor); //why not pass localPosition? Oversight?
				}
				currentPhysics.body = consumerBody
			}

			for (const fixture of deadFixtures) {
				otherBody.DestroyFixture(fixture);
			}

			if (consumerBody !== otherBody) {
				this.destroyBody(otherBody);
			}
		}
	}

	splitBodies(entity: Entity) {
		const physics = entity.getComponent(Physics);
		const entitySet = PhysicsSystem.bodyToEntitiesMap.get(physics.body);
		if (!entitySet || entitySet.size < 2) return;
		const connectedEntities = Array.from(entitySet);
		const combinedBody = physics.body;
		for (const link of connectedEntities) {
			entityTrace(link, `spitting out of a compound body`);
			const physics = link.getComponent(Physics);
			const transform = link.getComponent(Transform);
			const position = transform.position;
			physics.body = this.createBody(link, position, physics.body.GetAngle());
			if (physics.shapeType === ColliderShape.Circle) {
				physics.fixture = this.createCircleFixture(link, physics.body, physics.isSensor);
			} else {
				physics.fixture = this.createBoxFixture(link, physics.body, physics.isSensor);
			}
		}
		this.destroyBody(combinedBody);
		return connectedEntities;
	}

}

PhysicsSystem.queries = {
	physics: { components: [Physics], listen: { added: true, removed: true } },
	collisionsToCleanUp: { components: [Collision] },
	growEffects: { components: [Grow, Physics] }
};
